import type React from "react";
import {useState} from "react";
import {observer} from "mobx-react-lite";
import {Button, Stack, Tooltip, Typography} from "@mui/material";
import SendIcon from "@mui/icons-material/SendOutlined";
import {COLORS} from "~/style/colors-ts";
import {environment} from "~/env";
import {ACTION_BAR_HEIGHT} from "~/pages/taxonomy-builder-v2/constants/taxonomyBuilderLayout";
import {ApprovalStatusEnum} from "~/services/classes/AiClasses";
import type {m_taxonomy} from "~/services/classes/TaxonomyClasses";
import type {TaxonomyChangeRow} from "~/pages/taxonomy-builder-v2/services/taxonomyChanges";
import {useCanEditTaxonomy} from "~/pages/taxonomy-builder-v2/hooks/useCanEditTaxonomy";
import {YourChangesDialog} from "./YourChanges/YourChangesDialog";

type TaxonomyDraftActionBarProps = {
    /** The draft's changes; "Send for approval" opens them. */
    changes: TaxonomyChangeRow[];
    /** Undefined while loading */
    taxonomy?: m_taxonomy.FullSerializer;
};

/**
 * The design's bottom action bar (`m3-actionbar`), stuck to the bottom of the page: draft status on the
 * left, "Send for approval" on the right, which opens the draft's changes to review and send.
 */
export const TaxonomyDraftActionBar: React.FC<TaxonomyDraftActionBarProps> = observer(({changes, taxonomy}) => {
    const [changesOpen, setChangesOpen] = useState(false);
    const canEdit = useCanEditTaxonomy(taxonomy);
    const count = changes.length;
    const openRequest = taxonomy?.active_approval_request;
    const awaitingApproval = !!openRequest && !ApprovalStatusEnum.isConcluded(openRequest.current_status.status);

    // Why it can't be sent, as in the current builder (`canGotToApprovalErrorMessage`); empty when it can.
    const blockedReason = !taxonomy ? 'Loading…'
        // The sales demo can always send.
        : environment.package === 'sales_demo' ? ''
            : awaitingApproval ? 'Please resolve the current approval request before sending another one.'
                : !canEdit ? 'You cannot edit this taxonomy.'
                    : count === 0 ? 'No changes to send.'
                        : '';

    const status = awaitingApproval
        ? 'Sent for approval. The taxonomy can\'t be changed until the approval is complete.'
        : count === 0
            ? 'No draft changes. The active taxonomy is unchanged.'
            : `${count} ${count === 1 ? 'changed category' : 'changed categories'} in the draft.`;

    return (
        <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            gap={2}
            sx={{
                position: 'sticky',
                bottom: 0,
                zIndex: 1000,
                height: ACTION_BAR_HEIGHT,
                px: 3,
                borderTop: `1px solid ${COLORS.grey[200]}`,
                backgroundColor: COLORS.white[900],
            }}
        >
            <Typography variant="caption" color="text.secondary" noWrap minWidth={0}>{status}</Typography>
            <Tooltip title={blockedReason}>
                {/* A disabled button gets no hover events: the span keeps the reason showing. */}
                <span>
                    <Button
                        variant="contained"
                        startIcon={<SendIcon/>}
                        disabled={Boolean(blockedReason)}
                        onClick={() => setChangesOpen(true)}
                    >
                        Send for approval
                    </Button>
                </span>
            </Tooltip>
            {/* Mounted per opening, so the notes start empty. */}
            {changesOpen && taxonomy && (
                <YourChangesDialog rows={changes} taxonomy={taxonomy} onClose={() => setChangesOpen(false)}/>
            )}
        </Stack>
    );
});
